import { Component, computed, inject, linkedSignal, OnDestroy, OnInit, ResourceStatus } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { SiteActions, SiteSelectors } from '@next/core-lib/site';
import { UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';

import { catchError, combineLatest, distinctUntilChanged, map, of, startWith, Subject, switchMap } from 'rxjs';
import { Environment } from 'src/environments/environment.model';

import { AsyncPipe } from '@angular/common';
import { MatDivider } from '@angular/material/divider';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { PageToolbarComponent } from '@next/core-lib/components';
import { TranslatePipe } from '@next/core-lib/i18n';
import { ListComponent } from '../advices/components/dashboard-widget/list.component';
import { selectCompanySettings } from '../advices/store/selectors';
import { BfpDashboardWidgetComponent } from '../bem/dashboard-widget/bfp-dashboard-widget.component';
import {
  selectCropFieldNutrientCalculation,
  selectCustomerId as selectBfpCustomerId,
  selectLoadStatus as selectBfpLoadStatus,
} from '../bem/store/bfp.feature';
import { BlightDashboardWidgetComponent } from '../blight-v2/components/dashboard-widget/blight-dashboard-widget.component';
import { BlightSignalStore } from '../blight-v2/signal-store/blight-signal-store';
import { DownyMildewSignalStore } from '../downy-mildew/signal-store/downy-mildew-signal-store';
import { DownyMildewDashboardWidgetComponent } from '../downy-mildew/components/dashboard-widget/downy-mildew-dashboard-widget.component';
import { SyncFromFmsButtonComponent } from '@app/shared/fms-sync/components/sync-from-fms-button/sync-from-fms-button.component';
import { StockButtonComponent } from '@app/shared/stock/components/stock-button/stock-button.component';
import { SiteState } from '../shared/models';
import { SubscriptionAccessService } from '../shared/services/subscription-access.service';
import { loadCustomerBlightRights } from '../store/actions';
import { VirusMonitorDashboardWidgetComponent } from '../virus-monitor/components/dashboard-widget/virus-monitor-dashboard-widget.component';
import { VirusMonitorSignalStore } from '../virus-monitor/signal-store/virus-monitor-signal-store';
import { MapComponent } from './components/cropfield-map/map.component';
import { EIPDeliveriesWidgetComponent } from './components/environmental-impact/components/eip-deliveries-widget/eip-deliveries-widget.component';

import {
  EnvironmentalImpactPointsComponent,
} from './components/environmental-impact/components/environmental-impact-points/environmental-impact-points.component';
import { OrganicMatterBalanceComponent } from './components/organic-matter-balance/components/organic-matter-balance/organic-matter-balance.component';
import { CropYearSelectorComponent } from '../shared/components/crop-year-selector/crop-year-selector.component';

type InsightWidget = 'blight' | 'downyMildew' | 'virusMonitor' | 'bfp' | 'advices';
type WidgetContent = 'pending' | 'empty' | 'populated';

interface WidgetOrderSource {
  customerId: number | null;
  defaultWidgetsFirst: boolean;
  resolvedOrder: WidgetOrder | undefined;
}

interface WidgetOrder {
  widgetsFirst: boolean;
  resolved: boolean;
  emptyWidgetsLayout: boolean;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  imports: [
    PageToolbarComponent,
    MatDivider,
    BlightDashboardWidgetComponent,
    DownyMildewDashboardWidgetComponent,
    VirusMonitorDashboardWidgetComponent,
    BfpDashboardWidgetComponent,
    ListComponent,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MapComponent,
    EnvironmentalImpactPointsComponent,
    EIPDeliveriesWidgetComponent,
    OrganicMatterBalanceComponent,
    StockButtonComponent,
    SyncFromFmsButtonComponent,
    CropYearSelectorComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class DashboardComponent implements OnInit, OnDestroy {
  readonly env = inject<Environment>(EnvironmentInjector);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly subscriptionAccessService = inject(SubscriptionAccessService);
  readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);

  readonly showDownyMildewDashboardWidget = this.env.SHOW_DOWNY_MILDEW_DASHBOARD_WIDGET;
  readonly showVirusMonitorDashboardWidget = this.env.SHOW_VIRUS_MONITOR_DASHBOARD_WIDGET;
  private readonly blightStore = inject(BlightSignalStore);
  private readonly downyMildewStore = this.showDownyMildewDashboardWidget ? inject(DownyMildewSignalStore) : null;
  private readonly virusMonitorStore = this.showVirusMonitorDashboardWidget ? inject(VirusMonitorSignalStore) : null;
  private readonly customer = this.store.selectSignal(UserSelectors.selectCustomer);
  private readonly companySettings = this.store.selectSignal(selectCompanySettings);
  private readonly bfpFields = this.store.selectSignal(selectCropFieldNutrientCalculation);
  private readonly bfpLoadStatus = this.store.selectSignal(selectBfpLoadStatus);
  private readonly bfpCustomerId = this.store.selectSignal(selectBfpCustomerId);

  pageTitle$ = this.store.pipe(select(SiteSelectors.selectPageTitle));
  user$ = this.store.pipe(select(UserSelectors.selectUser));
  destroy$ = new Subject<boolean>();
  private readonly defaultOrder = toSignal(
    this.store.select(UserSelectors.selectCustomer).pipe(
      map((customer) => customer?.id ?? null),
      distinctUntilChanged(),
      switchMap((customerId) => {
        const pending = { customerId, widgetsFirst: false, loaded: false };
        if (customerId === null) return of(pending);
        return combineLatest([
          this.subscriptionAccessService.hasAccessToBem$,
          this.subscriptionAccessService.hasAccessToBlight$,
        ]).pipe(
          map(([hasBemAccess, hasBlightAccess]) => ({
            customerId,
            widgetsFirst: hasBemAccess || hasBlightAccess,
            loaded: true,
          })),
          startWith(pending),
          catchError(() => of({ ...pending, loaded: true })),
        );
      }),
    ),
    { initialValue: { customerId: null, widgetsFirst: false, loaded: false } },
  );

  private readonly widgetContent = computed<WidgetContent[]>(() => {
    const customerId = this.customer()?.id ?? null;
    const blightRights = this.blightStore.customerBlightRightsValue();
    let bfpContent: WidgetContent = 'pending';
    if (this.bfpLoadStatus() === 'loaded' && this.bfpCustomerId() === customerId) {
      bfpContent = this.bfpFields().length > 0 ? 'populated' : 'empty';
    }
    const content: WidgetContent[] = [
      isResourceResolved(this.blightStore.customerBlightRightsStatus()) && blightRights?.customerId === customerId
        ? getWidgetContent(
            blightRights.isPremium,
            this.blightStore.visibleBlightAdvices().length > 0,
            isResourceResolved(this.blightStore.blightDataStatus()) &&
              isResourceResolved(this.blightStore.participatingCropFieldsStatus()),
          )
        : 'pending',
      bfpContent,
    ];

    if (this.downyMildewStore) {
      content.push(
        this.downyMildewStore.moduleActivationStatus() === 'loaded'
          ? getWidgetContent(
              this.downyMildewStore.hasDownyMildewPremium(),
              this.downyMildewStore.downyMildewAdvices().length > 0,
              isResourceResolved(this.downyMildewStore.downyMildewDataStatus()),
            )
          : 'pending',
      );
    }
    if (this.virusMonitorStore) {
      content.push(
        this.virusMonitorStore.moduleActivationStatus() === 'loaded'
          ? getWidgetContent(
              this.virusMonitorStore.hasVirusMonitorPremium() === true,
              this.virusMonitorStore.hasVirusMonitorRegistrations(),
              isResourceResolved(this.virusMonitorStore.virusMonitorRegistrationsStatus()),
            )
          : 'pending',
      );
    }
    return content;
  });

  // Keep the last settled order during refresh/error, but never carry it to another customer.
  private readonly widgetOrder = linkedSignal<WidgetOrderSource, WidgetOrder>({
    source: () => {
      const customerId = this.customer()?.id ?? null;
      const defaults = this.defaultOrder();
      const settings = this.companySettings();
      const hasCurrentSettings = customerId !== null && settings?.customerId === customerId;
      const content = this.widgetContent();
      const canResolve =
        defaults.customerId === customerId &&
        defaults.loaded &&
        hasCurrentSettings &&
        (settings.automaticAdvice !== true || content.every((state) => state !== 'pending'));
      const emptyWidgetsLayout = settings?.automaticAdvice === true && content.every((state) => state === 'empty');

      return {
        customerId,
        defaultWidgetsFirst: defaults.customerId === customerId && defaults.widgetsFirst,
        resolvedOrder: canResolve
          ? { widgetsFirst: defaults.widgetsFirst && !emptyWidgetsLayout, resolved: true, emptyWidgetsLayout }
          : undefined,
      };
    },
    computation: (source, previous) => {
      if (source.resolvedOrder) return source.resolvedOrder;
      if (previous?.source.customerId === source.customerId && previous.value.resolved) {
        return previous.value;
      }
      return { widgetsFirst: source.defaultWidgetsFirst, resolved: false, emptyWidgetsLayout: false };
    },
  });

  readonly emptyWidgetsLayout = computed(() => this.widgetOrder().emptyWidgetsLayout);

  readonly orderedWidgets = computed<InsightWidget[]>(() => {
    const widgets: InsightWidget[] = ['blight'];
    if (this.showDownyMildewDashboardWidget) widgets.push('downyMildew');
    if (this.showVirusMonitorDashboardWidget) widgets.push('virusMonitor');
    widgets.push('bfp');
    return this.widgetOrder().widgetsFirst ? [...widgets, 'advices'] : ['advices', ...widgets];
  });

  // Convert observable to signal for reactive breakpoint detection
  isMobile = toSignal(this.breakpoint.ltMd$, { initialValue: false });

  ngOnInit() {
    this.store.dispatch(SiteActions.updatePageTitle({ pageTitle: 'dashboard' }));
    this.store.dispatch(loadCustomerBlightRights());
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}

function isResourceResolved(status: ResourceStatus): boolean {
  return status === 'resolved' || status === 'local';
}

function getWidgetContent(hasAccess: boolean, hasData: boolean, dataResolved: boolean): WidgetContent {
  if (!hasAccess) return 'empty';
  if (!dataResolved) return 'pending';
  return hasData ? 'populated' : 'empty';
}
